import '../../styles/candidate-palette.css';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { analyzeRoomGray, grayFromImageData } from '../../lib/roomViewSignals';
import { friendlyCameraError, roomCameraApiRoot, roomCameraBlockedReason, secureRoomCameraUrl } from '../../lib/roomCameraClient';

export default function RoomCameraPage() {
  const { t } = useTranslation('candidate');
  const { token: pathToken } = useParams();
  const [params] = useSearchParams();
  const initialToken = pathToken || params.get('token') || '';
  const tokenRef = useRef(initialToken);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previousGray = useRef<Uint8Array | null>(null);
  const baselineDesk = useRef<Uint8Array | null>(null);
  const startedAt = useRef(0);
  const [status, setStatus] = useState(initialToken ? 'ready' : 'missing');
  const [message, setMessage] = useState('');
  const [armed, setArmed] = useState(false);
  const [deskVisible, setDeskVisible] = useState(false);
  const [floorVisible, setFloorVisible] = useState(false);
  const [sideView, setSideView] = useState(false);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [canSwitch, setCanSwitch] = useState(false);
  const facingRef = useRef<'environment' | 'user'>('environment');
  const checklistReady = deskVisible && floorVisible && sideView;
  const secureHref = secureRoomCameraUrl(typeof window === 'undefined' ? '' : window.location.href);

  const attachStream = (stream: MediaStream) => {
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      void videoRef.current.play().catch(() => undefined);
    }
    setStatus('streaming');
    setArmed(true);
    if (navigator.mediaDevices?.enumerateDevices) {
      void navigator.mediaDevices.enumerateDevices().then((devices) => {
        const cameras = devices.filter((device) => device.kind === 'videoinput');
        setCanSwitch(cameras.length > 1);
      }).catch(() => setCanSwitch(false));
    }
  };

  const switchCamera = () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const next = facingRef.current === 'environment' ? 'user' : 'environment';
    const requestCamera = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    const pending = requestCamera({
      video: { facingMode: { ideal: next }, width: { ideal: 640 } },
      audio: false
    });
    void pending.then((stream) => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      facingRef.current = next;
      setFacing(next);
      attachStream(stream);
    }).catch((error: unknown) => {
      const name = error instanceof DOMException ? error.name : '';
      const constraintMiss = name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError' || name === 'NotFoundError';
      setMessage(constraintMiss
        ? t('roomCamera.otherCameraUnavailable', { defaultValue: 'The other camera is not available.' })
        : friendlyCameraError(error));
    });
  };

  const startFromClick = () => {
    const blocked = roomCameraBlockedReason(
      window.location.href,
      Boolean(navigator.mediaDevices?.getUserMedia)
    );
    if (blocked || !navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      setMessage(blocked || friendlyCameraError(new Error('navigator.mediaDevices.getUserMedia')));
      return;
    }
    const requestCamera = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    const rear = requestCamera({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 } },
      audio: false
    });
    setStatus('starting');
    void rear.then((stream) => {
      attachStream(stream);
    }).catch((error: unknown) => {
      const name = error instanceof DOMException ? error.name : '';
      const constraintMiss = name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError' || name === 'NotFoundError';
      if (!constraintMiss) {
        setStatus('error');
        setMessage(friendlyCameraError(error));
        return;
      }
      void requestCamera({ video: true, audio: false }).then((stream) => {
        attachStream(stream);
      }).catch((retryError: unknown) => {
        setStatus('error');
        setMessage(friendlyCameraError(retryError));
      });
    });
  };

  useEffect(() => {
    if (!armed || !streamRef.current) return;
    let timer = 0;
    let stopped = false;
    startedAt.current = Date.now();
    const wake = navigator.wakeLock?.request?.('screen').catch(() => undefined);

    const send = async () => {
        if (stopped) return;
        const video = videoRef.current;
        if (!video || video.readyState < 2) return;
        const canvas = document.createElement('canvas');
        canvas.width = 480;
        canvas.height = 360;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const gray = grayFromImageData(pixels.data, canvas.width, canvas.height);
        const baselineReady = Date.now() - startedAt.current >= 10_000;
        const signals = analyzeRoomGray(
          gray,
          canvas.width,
          canvas.height,
          previousGray.current,
          baselineReady ? baselineDesk.current : null
        );
        previousGray.current = gray;
        if (baselineReady && !baselineDesk.current) baselineDesk.current = signals.deskMask;
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.55));
        if (!blob) return;
        const body = new FormData();
        body.append('frame', blob, 'room.jpg');
        body.append('luma', String(Math.round(signals.luma)));
        body.append('scene_delta', String(signals.sceneDelta));
        body.append('person_count', String(signals.personCount));
        body.append('desk_object', signals.deskObject ? 'true' : 'false');
        body.append('page_visible', document.visibilityState === 'visible' ? 'true' : 'false');
        body.append('captured_at', new Date().toISOString());
        const response = await fetch(`${roomCameraApiRoot(window.location.href, String(import.meta.env.VITE_API_URL || ''))}/room-camera/frames`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${tokenRef.current}` },
          body
        });
        if (!response.ok) {
          setMessage(t('roomCamera.frameRejected', { defaultValue: 'Frame rejected ({{status}})', status: response.status }));
          return;
        }
        const payload = await response.json().catch(() => ({}));
        if (payload.device_token) tokenRef.current = payload.device_token;
        const hidden = document.visibilityState !== 'visible';
        setMessage(hidden
          ? t('roomCamera.paused', { defaultValue: 'Paused while the page is hidden' })
          : t('roomCamera.streaming', { defaultValue: 'Streaming' }));
        setStatus(hidden ? 'paused' : 'streaming');
      };

    timer = window.setInterval(() => {
      void send().catch((error) => setMessage(friendlyCameraError(error)));
    }, 2500);
    void send();

    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        setMessage(t('roomCamera.paused', { defaultValue: 'Paused while the page is hidden' }));
      }
    };
    document.addEventListener('visibilitychange', onHide);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      void wake;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [armed, t]);

  return (
    <main className="room-camera-experience mx-auto min-h-screen w-full max-w-lg bg-slate-950 px-4 py-5 text-slate-100 motion-reduce:transition-none">
      <h1 className="text-xl font-semibold">{t('roomCamera.phoneTitle', { defaultValue: 'Room camera' })}</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">
        {t('roomCamera.phonePurpose', {
          defaultValue: 'This page shows your desk, hands, and the floor beside the desk to the invigilator. It does not show exam questions. Keep this page open.'
        })}
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-300">
        <li>{t('roomCamera.placeSide', { defaultValue: 'Put the phone on the side of the desk, about 1–2 metres away, in landscape.' })}</li>
        <li>{t('roomCamera.placeShows', { defaultValue: 'The picture should include your torso, hands, the desk, and the floor under the table. Your face does not need to fill the frame.' })}</li>
      </ul>
      {status !== 'missing' && !armed && !secureHref && (
        <fieldset className="mt-4 space-y-2 text-sm">
          <legend className="font-semibold">{t('roomCamera.checklist', { defaultValue: 'Placement check' })}</legend>
          <label className="flex min-h-11 items-center gap-2">
            <input type="checkbox" className="h-5 w-5" checked={deskVisible} onChange={(event) => setDeskVisible(event.target.checked)} />
            {t('roomCamera.checkDesk', { defaultValue: 'The desk and my hands are visible' })}
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input type="checkbox" className="h-5 w-5" checked={floorVisible} onChange={(event) => setFloorVisible(event.target.checked)} />
            {t('roomCamera.checkFloor', { defaultValue: 'The floor under the table is visible' })}
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input type="checkbox" className="h-5 w-5" checked={sideView} onChange={(event) => setSideView(event.target.checked)} />
            {t('roomCamera.checkSide', { defaultValue: 'This is a side view, not a close-up of my face' })}
          </label>
          <button
            type="button"
            disabled={!checklistReady}
            className="mt-2 min-h-11 w-full rounded-xl candidate-action-primary px-4 py-3 text-sm font-semibold disabled:opacity-40"
            onClick={startFromClick}
          >
            {t('roomCamera.start', { defaultValue: 'Start room camera' })}
          </button>
        </fieldset>
      )}
      {secureHref && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-amber-200">
            {t('roomCamera.needHttps', {
              defaultValue: 'This page is open over HTTP. The phone hides the camera until the address starts with https://. Open the secure page, and if the phone warns about the certificate, continue to the site.'
            })}
          </p>
          <a
            className="candidate-action-primary block rounded-xl px-4 py-3 text-center text-sm font-semibold"
            href={secureHref}
          >
            {t('roomCamera.openSecure', { defaultValue: 'Open the secure camera page' })}
          </a>
        </div>
      )}
      {status === 'missing' && (
        <p className="mt-4 text-sm candidate-ink-danger">{t('roomCamera.missingToken', { defaultValue: 'This link has no pairing token. Scan the QR code on the exam computer again.' })}</p>
      )}
      <p className="mt-3 text-sm" role="status" aria-live="polite">{status}{message ? ` — ${message}` : ''}</p>
      <video ref={videoRef} className="mt-4 aspect-[4/3] w-full rounded-xl bg-black object-contain" playsInline muted autoPlay />
      {armed && canSwitch && (
        <button
          type="button"
          className="mt-3 min-h-11 w-full rounded-xl border candidate-action-secondary px-4 py-3 text-sm font-semibold"
          onClick={switchCamera}
        >
          {facing === 'environment'
            ? t('roomCamera.switchToFront', { defaultValue: 'Switch to front camera' })
            : t('roomCamera.switchToBack', { defaultValue: 'Switch to back camera' })}
        </button>
      )}
    </main>
  );
}
